import useSWR from 'swr';
import { useCallback, useMemo, useState } from 'react';
// utils
import { fetcher, endpoints, mutationFetcher } from 'src/utils/axios';
// types
import { IGetServices, IGetUsers } from 'src/types/product';


export function useGetUser(Page: number, Limit: number) {
    const user_id = localStorage.getItem('id')
 const URL = `${endpoints.users.getuserapi}?page=${Page}&per_page=${Limit}&user_id=${user_id}`;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
 
  const memoizedValue = useMemo(
    () => ({
      products: (data?.data as IGetUsers[]) || [],
      totalCount: (data?.total) || [],
      productsLoading: isLoading,
      productsError: error,
      productsValidating: isValidating,
      productsEmpty: !isLoading && !data?.products,
    }),
    [data, error, isLoading, isValidating]
  );
  

  return memoizedValue;
}


export function useGetAsmPermission(Page: number, Limit: number) {
    const user_id = localStorage.getItem('id')
 const URL = `${endpoints.asmpermission.getAsmList}?page=${Page}&per_page=${Limit}`;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      products: (data?.data as IGetUsers[]) || [],
      totalCount: (data?.total) || [],
      productsLoading: isLoading,
      productsError: error,
      productsValidating: isValidating,
      productsEmpty: !isLoading && !data?.products,
    }),
    [data, error, isLoading, isValidating]
  );
  

  return memoizedValue;
}


export function useGetServiceUser(page: number, perPage: number) {
  const URL = `${endpoints.service.getserviceapi}?page=${page}&per_page=${perPage}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      services: data?.data || [],
      totalCount: data?.total || 0,
      productsLoading: isLoading,
      productsError: error,
      productsValidating: isValidating,
      productsEmpty: !isLoading && !data?.data?.length,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}


export function useGetServiceById(serviceId?: string) {
  const shouldFetch = Boolean(serviceId);
  const URL = shouldFetch
    ? `${endpoints.service.getserviceByIdapi}?id=${serviceId}`
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR<IGetServices>(
    URL,
    fetcher,
    {
      revalidateOnMount: true,
      revalidateIfStale: true,
      keepPreviousData: false,
    }
  );

  const memoizedValue = useMemo(
    () => ({
      service: data || null,
      serviceLoading: isLoading,
      serviceError: error,
      serviceValidating: isValidating,
      serviceEmpty: !isLoading && !data,
      refetchService: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}




export function useGetUserById() {
  const userId = localStorage.getItem("id")
  const URL = endpoints.users.getByIdUser(userId)

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      getuserbyid: (data as IGetServices[]) || [],
      productsLoading: isLoading,
      productsError: error,
      productsValidating: isValidating,
      productsEmpty: !isLoading && !data?.length,
      mutate, 
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}


export function useGetAllCompany() {
  const userId = localStorage.getItem("id")
  const URL = endpoints.comany.allcompanyId

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      getCompanyid: (data as IGetServices[]) || [],
      productsLoading: isLoading,
      productsError: error,
      productsValidating: isValidating,
      productsEmpty: !isLoading && !data?.length,
      mutate, 
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}



export function useUpdateService() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<any>(null);

  const updateService = useCallback(
    async (serviceId: number, name: string) => {
      setLoading(true);
      setError(null);

      try {
        const url = `${endpoints.service.putservice}/${serviceId}`;
        const payload = { name };
        const res = await mutationFetcher([url, payload, 'PUT']);
        if (res && res.data && res.data.message) {
          setResponse(res.data.message);
        } else {
          setResponse('Service updated successfully');
        }

        return res;
      } catch (err: any) {
        setError(err.message || 'Unknown error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    updateService, 
    loading,
    error,
    response,
  };
}



export function useCreateService() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<any>(null);

  const createService = useCallback(
    async (name: string, created_by: string, category: string) => {
      setLoading(true);
      setError(null);
      try {
        const url = endpoints.service.postservice;
        const payload = {
          name,
          created_by,
          category,
        };
        const res = await mutationFetcher([url, payload, 'POST']);
        if (res && res.data && res.data.message) {
          setResponse(res.data.message);
        } else {
          setResponse('Service created successfully');
        }

        return res;
      } catch (err: any) {
        setError(err.message || 'Unknown error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    createService,
    loading,
    error,
    response,
  };
}



export function useTokenList(tokenId?: string | number) {
  const shouldFetch = Boolean(tokenId);
  const URL = shouldFetch ? `${endpoints.users.tokenListUser}/${tokenId}` : null;

  const { data, error, isLoading, isValidating } = useSWR(URL, fetcher);

  return {
    token1: data?.token ?? null,               
    tokenLoading: isLoading,
    tokenError: error,
    tokenValidating: isValidating,
    tokenEmpty: !isLoading && !data?.token,
  };
}

// dashbord api

export function useGetDashboard(
  selectedDays: string,
  from_date?: string,
  to_date?: string,
  user_id?:any,
  company_id?: number
) {
  const localStorageRole = localStorage.getItem('role');
  const servicesEnabled = localStorage.getItem("servicesEnabled");

      const id = company_id;

  let URL = null;
  if (selectedDays === 'custom' && from_date && to_date) {
        URL = id
      ? `${endpoints.alert.apiforDashboard}?company_id=${id}&user_id=${user_id}&from_date=${from_date}&to_date=${to_date}`
      : `${endpoints.alert.apiforDashboard}?user_id=${user_id}&from_date=${from_date}&to_date=${to_date}`;
  } else {
     URL = id
      ? `${endpoints.alert.apiforDashboard}?company_id=${id}&user_id=${user_id}&filter=${selectedDays}`
      : `${endpoints.alert.apiforDashboard}?user_id=${user_id}&filter=${selectedDays}`;
  }
  const { data, error, isLoading, isValidating, mutate } = useSWR(URL, fetcher);
  return {
    dashboardData: data ?? {},
    loading: isLoading,
    error,
    validating: isValidating,
    empty: !isLoading && !data,
    mutate,
    inProgress: data?.in_progress ?? 0,
    resolved: data?.resolved ?? 0,
    rejected: data?.rejected ?? 0,
    yetToConfirm: data?.yet_to_cnf ?? 0,
    requestPending: data?.request_pending ?? 0,
    serviceCounts: data?.services_counts ?? [],
  };
}



export function useUploadZipFile() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<any>(null);

  const uploadZip = useCallback(
    async (file: File) => {
      console.log('Uploading ZIP file:', file); // ✅ debug
      setLoading(true);
      setError(null);

      try {
        const url = endpoints.alert.zipUplode; 
        const formData = new FormData();
        formData.append('zip_file', file); 

        const res = await mutationFetcher([url, formData, 'POST']);
        setResponse(res);
        return res;
      } catch (err: any) {
        setError(err.message || 'Unknown error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    uploadZip,
    loading,
    error,
    response,
  };
}

 

export function useGetServiceDetailsById(companyId?: string | number) {
  const shouldFetch = Boolean(companyId);
  const URL = shouldFetch
    ? `${endpoints.service.serviceCompany}?company_id=${companyId}`
    : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  return {
    services: data?.services || [], // ✅ Fixed here
    servicesLoading: isLoading,
    servicesError: error,
    servicesValidating: isValidating,
    servicesEmpty: !isLoading && !data?.services?.length,
  };
}



export function useUserGetById(userId?: number) {
  const shouldFetch = Boolean(userId);
  const URL = shouldFetch ? `${endpoints.users.getUserId}/${userId}` : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      user: data?.user ?? null,
      userLoading: isLoading,
      userError: error,
      userValidating: isValidating,
      userEmpty: !isLoading && !data,
      refetchUser: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

